import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import cn from 'classnames';
import {LocationPoint} from '@/common/interfaces/location';
import {useSelectKeyboardControl} from '@/common/helpers/hooks/useSelectKeyboardControl';
import {useLocalizer} from '@/common/services/localizer';
import {FieldText, FieldTextProps} from '@/common/components/FieldText';
import styles from './LocationSearch.css';

export interface LocationSearchRef {
	reset(): void;
	setValue(value: string): void;
}

interface LocationSearchProps extends Omit<FieldTextProps, 'ref' | 'onSelect' | 'onFocus' | 'onBlur'> {
	className?: string;
	fieldName?: string;
	placeholder?: string;
	fetchSuggest(searchString: string): Promise<LocationPoint[]>;
	onSelect(location: LocationPoint, params: {target: HTMLInputElement}): void;
	onFocus?(event: React.FocusEvent<HTMLInputElement> | FocusEvent): void;
	onBlur?(event: React.FocusEvent<HTMLInputElement> | FocusEvent): void;
}

const preventEventDefault = (event: React.SyntheticEvent<any>) => {
	event.preventDefault();
};

export const LocationSearch = forwardRef<LocationSearchRef, LocationSearchProps>((props, externalRef) => {
	const {className, fieldName, placeholder, fetchSuggest, defaultValue, onSelect, onChange, ...proxyProps} = props;

	const fieldRef = useRef<HTMLInputElement>(null);

	const [isFocused, setFocused] = useState(false);
	const [searchValue, setSearchValue] = useState(defaultValue || '');
	const [searchResult, setSearchResult] = useState<LocationPoint[]>([]);
	const [loading, setLoading] = useState(false);

	const initialSearchValue = useMemo(() => searchValue, []);

	useImperativeHandle(
		externalRef,
		() => ({
			reset() {
				setSearchValue('');
				setSearchResult([]);
			},
			setValue(value) {
				setSearchValue(value);
				setSearchResult([]);
			},
		}),
		[],
	);

	const t = useLocalizer();

	const listLength = searchResult.length;
	const {focusedIndex, handleKeyboardControlEvent, handleFocusIndexReset} = useSelectKeyboardControl({listLength});

	const handleSearchValueChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.currentTarget.value;
			setSearchValue(value);

			if (value) {
				setLoading(true);
				fetchSuggest(value)
					.then(result => {
						setSearchResult(result);
						setLoading(false);
					})
					.catch(() => setLoading(false));
			} else {
				setSearchResult([]);
			}

			if (onChange) {
				onChange(event);
			}
		},
		[t.language, onChange],
	);

	const handleSearchValueClear = () => {
		setSearchValue('');
		setSearchResult([]);
		fieldRef.current!.focus();
	};

	const handleSuggestSelect = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			const id = event.currentTarget.dataset.id;
			const location = searchResult.find(item => item.id === id);
			if (location) {
				handleFocusIndexReset();
				setSearchResult([]);
				setSearchValue(location.title);
				onSelect(location, {target: fieldRef.current!});
			}
		},
		[searchResult, onSelect, handleFocusIndexReset],
	);

	// Select focused or the first location from suggest on Enter key down
	const handleFieldKeydown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				if (searchResult.length) {
					const location = searchResult[focusedIndex] || searchResult[0];
					handleFocusIndexReset();
					setSearchResult([]);
					setSearchValue(location.title);
					onSelect(location, {target: fieldRef.current!});
				}
				return;
			}

			if (searchResult.length) {
				handleKeyboardControlEvent(event);
			}
		},
		[searchResult, focusedIndex, handleKeyboardControlEvent, onSelect],
	);

	const handleFocusIn = useCallback(
		(event: React.FocusEvent<HTMLInputElement> | FocusEvent) => {
			setFocused(true);
			if (proxyProps.onFocus) {
				proxyProps.onFocus(event);
			}
		},
		[proxyProps.onFocus],
	);

	const handleFocusOut = useCallback(
		(event: React.FocusEvent<HTMLInputElement> | FocusEvent) => {
			setFocused(false);
			if (proxyProps.onBlur) {
				proxyProps.onBlur(event);
			}
		},
		[proxyProps.onBlur],
	);

	// Reset focused item on the list change
	useEffect(() => {
		if (focusedIndex !== -1) {
			handleFocusIndexReset();
		}
	}, [searchValue]);

	useEffect(() => {
		// Workaround to React bug with old Firefox version (<= 54) - no onFocus event triggers
		const ua = navigator.userAgent;
		if (ua.includes('Firefox/') && !ua.includes('Seamonkey/')) {
			const ffVersion = /Firefox\/(\w+)/.exec(ua)?.[1];
			if (!(Number(ffVersion) <= 54)) {
				return;
			}
		} else {
			return;
		}

		fieldRef.current!.addEventListener('focus', handleFocusIn);
		fieldRef.current!.addEventListener('blur', handleFocusOut);
		fieldRef.current!.blur();
		fieldRef.current!.focus();
	}, []);

	return (
		<div className={cn(styles.root, className)}>
			<FieldText
				ref={fieldRef}
				className={styles.field}
				name={fieldName}
				value={searchValue}
				autoFocus
				autoComplete='off'
				placeholder={placeholder || t('searchSettlementHint')}
				maxLength={100}
				onChange={handleSearchValueChange}
				onKeyDown={handleFieldKeydown}
				{...proxyProps}
				onFocus={handleFocusIn}
				onBlur={handleFocusOut}
			/>
			{!!searchValue && <button className={styles.clearButton} type='button' onClick={handleSearchValueClear} />}
			{initialSearchValue !== searchValue && searchResult.length === 0 && isFocused && !loading && (
				<p className={styles.notFoundPlaceholder}>{t('searchNotFound')}</p>
			)}
			{!!searchResult.length && isFocused && (
				<ul className={styles.list}>
					{searchResult.map(({id, title, description}, index) => (
						<li key={id}>
							<button
								className={cn(styles.itemButton, index === focusedIndex && styles.isFocused)}
								type='button'
								data-id={id}
								title={`${title}, ${description}`}
								onClick={handleSuggestSelect}
								onMouseDown={preventEventDefault}>
								{title}
								<span className={styles.itemDescription}>, {description}</span>
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
});

LocationSearch.displayName = 'LocationSearch';
